import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { adminSessionSchema, plainSessionSchema } from '../session.js';
import { sessionFeedbackIssueSchema } from '../session-feedbacks.js';
import { orgFeedbackTypeSchema } from '../org-shared.js';
import { coachUserSchema } from '../coach.js';
import { paginatedResponse } from '../util-schema-types.js';
import { getPayCycleFilterSchema, payCycleSchema } from './pay-cycle.js';
import { userSchema } from '../user.js';
import { pagingSchema, filteringSchemaFrom } from '../modifier.js';
import { paymentAccountType } from '../payment-account-type.js';
import {
  accountClassEnum,
  accountTypeEnum,
  brexVendorACHSchema,
  brexVendorDomesticWireSchema,
  brexVendorInternationalWireSchema,
  brexVendorSchema,
  brexTransferCounterpartySchema,
  brexTransferSchema,
  paymentAccountTypeKind,
  transferStatusKind,
  vendorChequeSchema,
} from './brex.js';
import { Summary, SummaryLike } from '../../summary.js';

export const payableSessionSchema = plainSessionSchema.extend({
  isCompleted: z.boolean(),
  isCompletedOnTime: z.boolean(),
  isFeedbackSubmittedOnTime: z.boolean(),
  orgFeedbackType: orgFeedbackTypeSchema,
  studentAttendances: z.array(z.boolean().nullable()),
  issues: sessionFeedbackIssueSchema.array(),
});
export type PayableSession = z.infer<typeof payableSessionSchema>;

export const coachSessionPaymentLikeSchema = z.object({
  coachId: z.string(),
  session: payableSessionSchema,
});
export type CoachSessionPaymentLike = z.infer<typeof coachSessionPaymentLikeSchema>;

export const coachSessionPaymentRollupSchema = z.object({
  payments: coachSessionPaymentLikeSchema.array(),
  totalAmountCent: z.number(),
  payPeriodId: z.string(),
  payPlainDate: z.instanceof(Temporal.PlainDate),
});

export type CoachSessionPaymentRollup = z.infer<typeof coachSessionPaymentRollupSchema>;

export { paymentAccountType, paymentAccountTypeToLabel } from '../payment-account-type.js';
export type { PaymentAccountType } from '../payment-account-type.js';

export const coachPaymentKind = z.enum([
  'teaching',
  'incentive',
  'adjustment',
  'referral',
  'workshop',
  'mentoring',
  'reimbursement',
  'teacher_support',
  'curriculum_support',
  'school_support',
  'class_adjustment',
  'new_teacher_onboarding',
  'pay_in_lieu_of_notice',
  'extended_teaching',
  'admin_cancellations',
  'outreach_and_community',
  'family_teaching',
]);

export type CoachPaymentKind = z.infer<typeof coachPaymentKind>;

export const coachPaymentNoteKind = z.enum([
  'send_manually',
  'account_issue',
  'name_issue',
  'no_bank_info',
  'overpaid',
  'overpayment_adjusted',
]);

export type CoachPaymentNote = z.infer<typeof coachPaymentNoteKind>;

export const CoachPaymentNoteToLabel = {
  [coachPaymentNoteKind.enum.send_manually]: 'Send manually',
  [coachPaymentNoteKind.enum.account_issue]: 'Account issue',
  [coachPaymentNoteKind.enum.name_issue]: 'Name issue',
  [coachPaymentNoteKind.enum.no_bank_info]: 'No bank info',
  [coachPaymentNoteKind.enum.overpaid]: 'Overpaid',
  [coachPaymentNoteKind.enum.overpayment_adjusted]: 'Overpayment - adjusted',
};

export const coachPaymentStatusKind = z.enum([
  'not_started',
  'processing',
  'scheduled',
  'pending_approval',
  'processed',
  'failed',
]);

export type CoachPaymentStatus = z.infer<typeof coachPaymentStatusKind>;

export const unchangableCoachPaymentStatus = coachPaymentStatusKind.extract([
  'pending_approval',
  'processed',
  'scheduled',
  'processing',
]);

export const coachPaymentStatusToLabel = {
  [coachPaymentStatusKind.enum.not_started]: 'Unpaid',
  [coachPaymentStatusKind.enum.processing]: 'Processing',
  [coachPaymentStatusKind.enum.scheduled]: 'Scheduled',
  [coachPaymentStatusKind.enum.pending_approval]: 'Pending_approval',
  [coachPaymentStatusKind.enum.processed]: 'Sent',
  [coachPaymentStatusKind.enum.failed]: 'Failed',
};

export const coachPaymentStatusToColor = {
  [coachPaymentStatusKind.enum.not_started]: '#8E8E93',
  [coachPaymentStatusKind.enum.processing]: '#EA580C',
  [coachPaymentStatusKind.enum.scheduled]: '#EA580C',
  [coachPaymentStatusKind.enum.pending_approval]: '#EA580C',
  [coachPaymentStatusKind.enum.processed]: '#16A34A',
  [coachPaymentStatusKind.enum.failed]: '#EB404A',
};

export const brexTransferStatusToCoachPaymentStatus = {
  [transferStatusKind.enum.PROCESSING]: coachPaymentStatusKind.enum.processing,
  [transferStatusKind.enum.SCHEDULED]: coachPaymentStatusKind.enum.scheduled,
  [transferStatusKind.enum.PENDING_APPROVAL]: coachPaymentStatusKind.enum.pending_approval,
  [transferStatusKind.enum.PROCESSED]: coachPaymentStatusKind.enum.processed,
  [transferStatusKind.enum.FAILED]: coachPaymentStatusKind.enum.failed,
};

export const coachNonSessionPaymentKind = coachPaymentKind.exclude(['teaching']);

export type CoachNonSessionPaymentKind = z.infer<typeof coachNonSessionPaymentKind>;

export const coachPaymentKindToLabel = {
  [coachPaymentKind.enum.teaching]: 'Teaching',
  [coachPaymentKind.enum.incentive]: 'Incentive',
  [coachPaymentKind.enum.adjustment]: 'Payment adjustment',
  [coachPaymentKind.enum.referral]: 'Referral incentive',
  [coachPaymentKind.enum.workshop]: 'Workshop',
  [coachPaymentKind.enum.mentoring]: '1:1 teacher support',
  [coachPaymentKind.enum.reimbursement]: 'Reimbursement',
  [coachPaymentKind.enum.teacher_support]: 'Teacher support',
  [coachPaymentKind.enum.curriculum_support]: 'Curriculum support',
  [coachPaymentKind.enum.school_support]: 'School support',
  [coachPaymentKind.enum.class_adjustment]: 'Class adjustment',
  [coachPaymentKind.enum.new_teacher_onboarding]: 'New teacher onboarding',
  [coachPaymentKind.enum.pay_in_lieu_of_notice]: 'Pay in lieu of notice',
  [coachPaymentKind.enum.extended_teaching]: 'Extended teaching',
  [coachPaymentKind.enum.admin_cancellations]: 'Admin cancellations',
  [coachPaymentKind.enum.outreach_and_community]: 'Outreach and community',
  [coachPaymentKind.enum.family_teaching]: 'Family teaching',
} as const;

export const coachPaymentKindToBgColor = {
  [coachPaymentKind.enum.teaching]: '#dbeafe',
  [coachPaymentKind.enum.incentive]: '#f3e8ff',
  [coachPaymentKind.enum.adjustment]: '#dcfce7',
  [coachPaymentKind.enum.referral]: '#ffedd5',
  [coachPaymentKind.enum.workshop]: '#ecfccb',
  [coachPaymentKind.enum.mentoring]: '#fef9c3',
  [coachPaymentKind.enum.reimbursement]: '#fdf2f8',
  [coachPaymentKind.enum.teacher_support]: '#f3f4f6',
  [coachPaymentKind.enum.curriculum_support]: '#f3f4f6',
  [coachPaymentKind.enum.school_support]: '#f3f4f6',
  [coachPaymentKind.enum.class_adjustment]: '#f3f4f6',
  [coachPaymentKind.enum.new_teacher_onboarding]: '#f3f4f6',
  [coachPaymentKind.enum.pay_in_lieu_of_notice]: '#f3f4f6',
  [coachPaymentKind.enum.extended_teaching]: '#f3f4f6',
  [coachPaymentKind.enum.admin_cancellations]: '#f3f4f6',
  [coachPaymentKind.enum.outreach_and_community]: '#f3f4f6',
  [coachPaymentKind.enum.family_teaching]: '#f3f4f6',
} as const;

export const coachPaymentKindToTextColor = {
  [coachPaymentKind.enum.teaching]: '#2563eb',
  [coachPaymentKind.enum.incentive]: '#9333ea',
  [coachPaymentKind.enum.adjustment]: '#15803d',
  [coachPaymentKind.enum.referral]: '#ea580c',
  [coachPaymentKind.enum.workshop]: '#4d7c0f',
  [coachPaymentKind.enum.mentoring]: '#a16207',
  [coachPaymentKind.enum.reimbursement]: '#ec4899',
  [coachPaymentKind.enum.teacher_support]: '#6b7280',
  [coachPaymentKind.enum.curriculum_support]: '#6b7280',
  [coachPaymentKind.enum.school_support]: '#6b7280',
  [coachPaymentKind.enum.class_adjustment]: '#6b7280',
  [coachPaymentKind.enum.new_teacher_onboarding]: '#6b7280',
  [coachPaymentKind.enum.pay_in_lieu_of_notice]: '#6b7280',
  [coachPaymentKind.enum.extended_teaching]: '#6b7280',
  [coachPaymentKind.enum.admin_cancellations]: '#6b7280',
  [coachPaymentKind.enum.outreach_and_community]: '#6b7280',
  [coachPaymentKind.enum.family_teaching]: '#6b7280',
} as const;

export const coachPaymentSchema = z.object({
  id: z.string(),
  payCycleId: z.string(),
  payCycle: z.lazy(() => payCycleSchema),
  coachId: z.string(),
  coach: coachUserSchema,
  paymentDateOverride: z.date().optional(),
  paymentStatus: coachPaymentStatusKind,
});

export type CoachPayment = z.infer<typeof coachPaymentSchema>;

export const coachNonSessionPaymentSchema = z.object({
  id: z.string(),
  coachPaymentId: z.string(),
  amountInCents: z.number(),
  type: coachNonSessionPaymentKind,
  createdById: z.string().nullable(),
  createdBy: userSchema.nullable(),
});

export type CoachNonSessionPayment = z.infer<typeof coachNonSessionPaymentSchema>;

export const coachNonSessionPaymentExtendedSchema = coachNonSessionPaymentSchema.extend({
  coachPayment: coachPaymentSchema,
});

export type CoachNonSessionPaymentExtended = z.infer<typeof coachNonSessionPaymentExtendedSchema>;

export const coachSessionPaymentSchema = z.object({
  id: z.string(),
  coachId: z.string().nullish(),
  sessionId: z.string().nullish(),
  coachPaymentId: z.string().nullish(),
  paymentAmountInCents: z.number(),
});

export type CoachSessionPayment = z.infer<typeof coachSessionPaymentSchema>;

export const coachPaymentIncentiveNameSchema = z.enum(['completion', 'lateSubstitution']);
export type CoachPaymentIncentiveName = z.infer<typeof coachPaymentIncentiveNameSchema>;

export const coachSessionIncentiveSchema = z.object({
  id: z.string(),
  coachSessionPaymentId: z.string(),
  type: coachPaymentIncentiveNameSchema,
  bonusAmountInCents: z.number(),
});

export type CoachSessionIncentive = z.infer<typeof coachSessionIncentiveSchema>;

export const coachSessionPaymentWithIncentiveSchema = coachSessionPaymentSchema.extend({
  incentives: coachSessionIncentiveSchema.array(),
});

export const coachPaymentDepositInfoStatusSchema = z.enum(['ready', 'not_ready', 'missing_or_invalid']);
export type CoachPaymentDepositInfoStatus = z.infer<typeof coachPaymentDepositInfoStatusSchema>;

export const CoachPaymentDepositInfoStatusToLabel = {
  [coachPaymentDepositInfoStatusSchema.enum.not_ready]: 'Direct Deposit not set up yet',
  [coachPaymentDepositInfoStatusSchema.enum.ready]: 'Direct Deposit setup ready',
  [coachPaymentDepositInfoStatusSchema.enum.missing_or_invalid]: 'Bank account info missing or invalid',
};

export function localDepositInfoStatus(
  vendorId: string | null | undefined,
  paymentInstrumentId: string | null | undefined,
): CoachPaymentDepositInfoStatus {
  const hasVendorId = Boolean(vendorId?.trim());
  const hasPaymentInstrumentId = Boolean(paymentInstrumentId?.trim());

  if (!hasVendorId) {
    return coachPaymentDepositInfoStatusSchema.enum.not_ready;
  }
  if (!hasPaymentInstrumentId) {
    return coachPaymentDepositInfoStatusSchema.enum.missing_or_invalid;
  }
  return coachPaymentDepositInfoStatusSchema.enum.ready;
}

export const brexDepositVerificationKindSchema = z.enum([
  'ready',
  'vendor_missing',
  'instrument_missing',
  'unavailable',
]);
export type BrexDepositVerificationKind = z.infer<typeof brexDepositVerificationKindSchema>;

export function depositInfoStatusAfterBrexVerification(
  kind: BrexDepositVerificationKind,
  localStatus: CoachPaymentDepositInfoStatus,
): CoachPaymentDepositInfoStatus {
  switch (kind) {
    case brexDepositVerificationKindSchema.enum.vendor_missing:
      return coachPaymentDepositInfoStatusSchema.enum.not_ready;
    case brexDepositVerificationKindSchema.enum.instrument_missing:
      return coachPaymentDepositInfoStatusSchema.enum.missing_or_invalid;
    case brexDepositVerificationKindSchema.enum.ready:
      return coachPaymentDepositInfoStatusSchema.enum.ready;
    case brexDepositVerificationKindSchema.enum.unavailable:
      return localStatus;
  }
}

export type CoachSessionPaymentWithIncentive = z.infer<typeof coachSessionPaymentWithIncentiveSchema>;

export const coachPaymentReportSchema = z.object({
  id: z.string(),
  coach: coachUserSchema,
  payCycleId: z.string(),
  totalAmountInCents: z.number(),
  type: coachPaymentKind,
  totalSessionsTaught: z.number().optional(),
  payPeriodInterval: z.string(),
  payDate: z.string(),
  depositPostedDate: z.string().optional(),
  paymentStatus: coachPaymentStatusKind,
  paySentId: z.string().optional(),
  isLatestTeachingPayment: z.boolean().optional(),
  notes: z.array(coachPaymentNoteKind),
  depositInfoStatus: coachPaymentDepositInfoStatusSchema,
  paymentInstrumentId: z.string().nullable(),
});

export type CoachPaymentReport = z.infer<typeof coachPaymentReportSchema>;

export const coachPaymentRecipientSchema = coachUserSchema.pick({
  id: true,
  piiEntityType: true,
  email: true,
  firstName: true,
  lastName: true,
  fullNameOrEmail: true,
  timeZone: true,
  phone: true,
  overallGrade: true,
});

export type CoachPaymentRecipient = z.infer<typeof coachPaymentRecipientSchema>;

export const coachPaymentReportForRecipientSchema = coachPaymentReportSchema
  .omit({ paymentInstrumentId: true })
  .extend({ coach: coachPaymentRecipientSchema });

export type CoachPaymentReportForRecipient = z.infer<typeof coachPaymentReportForRecipientSchema>;

export const getCoachesPaymentReportResultSchema = paginatedResponse.extend({
  payments: z.array(coachPaymentReportSchema),
});

export type GetCoachesPaymentReportResult = z.infer<typeof getCoachesPaymentReportResultSchema>;

export const coachPaymentReportForPayCycleSchema = paginatedResponse.extend({
  payments: z.array(coachPaymentReportSchema),
});

export type CoachPaymentReportForPayCycle = z.infer<typeof coachPaymentReportForPayCycleSchema>;

export const getCoachPaymentResultSchema = paginatedResponse.extend({
  payments: z.array(coachPaymentReportForRecipientSchema),
});

export type GetCoachPaymentResult = z.infer<typeof getCoachPaymentResultSchema>;

export const createNonSessionPaymentSchema = z.object({
  coachId: z.string(),
  amount: z.number(),
  payCycleId: z.string(),
  type: coachNonSessionPaymentKind,
  createdById: z.string(),
  force: z.boolean().optional(),
});

export type CreateNonSessionPayment = z.infer<typeof createNonSessionPaymentSchema>;

export const updateNonSessionPaymentSchema = createNonSessionPaymentSchema.pick({
  amount: true,
  type: true,
  payCycleId: true,
});

export type UpdateNonSessionPayment = z.infer<typeof updateNonSessionPaymentSchema>;

export const getCoachPaymentFilterSchema = z.object({
  coachId: z.string(),
  ongoing: z.boolean().optional(),
  previous: z.boolean().optional(),
  future: z.boolean().optional(),
  payDateStartOnOrAfterPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  payDateStartOnOrBeforePlainDate: z.instanceof(Temporal.PlainDate).optional(),
});

export type GetCoachPaymentFilter = z.infer<typeof getCoachPaymentFilterSchema>;

export const getCoachPaymentModifierSchema = pagingSchema.merge(filteringSchemaFrom(getCoachPaymentFilterSchema));

export type GetCoachPaymentModifier = z.infer<typeof getCoachPaymentModifierSchema>;

export const getCoachPaymentAsCoachModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachPaymentFilterSchema.omit({ coachId: true })),
);

export type GetCoachPaymentAsCoachModifier = z.infer<typeof getCoachPaymentAsCoachModifierSchema>;

export const getCoachSessionPaymentFilterSchema = z.object({
  payCycleId: z.string(),
});

export type GetCoachSessionPaymentFilter = z.infer<typeof getCoachSessionPaymentFilterSchema>;

export const getCoachSessionPaymentFilterModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachSessionPaymentFilterSchema),
);

export type GetCoachSessionPaymentFilterModifier = z.infer<typeof getCoachSessionPaymentFilterModifierSchema>;

export const getCoachSessionPaymentModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachSessionPaymentFilterSchema),
);

export type GetCoachSessionPaymentModifier = z.infer<typeof getCoachSessionPaymentModifierSchema>;

export const getTotalSumFilterSchema = filteringSchemaFrom(
  z.object({
    coachId: z.string().optional(),
    payDateStartOnOrAfterPlainDate: z.instanceof(Temporal.PlainDate).optional(),
    payDateStartOnOrBeforePlainDate: z.instanceof(Temporal.PlainDate).optional(),
    payCycleInterval: z.string().optional(),
    query: z.string().optional().catch(''),
    type: z.array(z.lazy(() => coachPaymentKind)).optional(),
    payDate: z.string().optional(),
    paymentSentStatus: z.array(z.lazy(() => coachPaymentStatusKind)).optional(),
    depositInfoStatus: z.array(z.lazy(() => coachPaymentDepositInfoStatusSchema)).optional(),
    notes: z.array(z.lazy(() => coachPaymentNoteKind)).optional(),
  }),
);

export type GetTotalSumModifier = z.infer<typeof getTotalSumFilterSchema>;

export const coachPaymentSessionDetailsSchema = adminSessionSchema.extend({
  incentivesAmount: z.number().default(0),
  baseAmount: z.number().default(0),
});

export type CoachPaymentSessionDetails = z.infer<typeof coachPaymentSessionDetailsSchema>;

export const paymentByClassDurationSchema = z.object({
  '30min': z.number(),
  '40min': z.number(),
});

export type PaymentByClassDuration = z.infer<typeof paymentByClassDurationSchema>;

export const getCoachSessionPaymentDetailsResultSchema = paginatedResponse.extend({
  totalAmountInCents: z.number(),
  totalBaseAmountInCents: paymentByClassDurationSchema,
  totalIncentivesAmountInCents: paymentByClassDurationSchema,
  totalScheduledSessionHours: paymentByClassDurationSchema,
  depositPostedDate: z.string(),
  payments: z.array(coachPaymentSessionDetailsSchema),
});

export type GetCoachSessionPaymentDetailsResult = z.infer<typeof getCoachSessionPaymentDetailsResultSchema>;

export const syncCoachVendorSchema = z.object({
  paymentInstrumentId: z.string().optional(),
  vendorId: z.string(),
});

export type SyncCoachVendor = z.infer<typeof syncCoachVendorSchema>;

const maskedAccountNumberFields = {
  masked_routing_number: z.string().trim(),
  masked_account_number: z.string().trim(),
};

export const maskedVendorACHSchema = brexVendorACHSchema
  .omit({ routing_number: true, account_number: true })
  .extend(maskedAccountNumberFields);

export const maskedVendorDomesticWireSchema = brexVendorDomesticWireSchema
  .omit({ routing_number: true, account_number: true })
  .extend(maskedAccountNumberFields);

export const maskedVendorInternationalWireSchema = brexVendorInternationalWireSchema
  .omit({ iban: true, swift_code: true })
  .extend({
    masked_iban: z.string().trim(),
    masked_swift_code: z.string().trim(),
  });

export const maskedBrexTransferSchema = brexTransferSchema.extend({
  counterparty: brexTransferCounterpartySchema
    .omit({ routing_number: true, account_number: true })
    .extend(maskedAccountNumberFields)
    .optional(),
});

export type MaskedBrexTransfer = z.infer<typeof maskedBrexTransferSchema>;

export const vendor = brexVendorSchema.extend({
  payment_accounts: z
    .object({
      details: z.discriminatedUnion('type', [
        maskedVendorACHSchema,
        maskedVendorDomesticWireSchema,
        vendorChequeSchema,
        maskedVendorInternationalWireSchema,
      ]),
    })
    .optional(),
});

export type Vendor = z.infer<typeof vendor>;

export const coachVendorConnectionSchema = z.object({
  vendor: vendor.optional(),
  depositInfoStatus: coachPaymentDepositInfoStatusSchema,
  verificationUnavailable: z.boolean(),
});

export type CoachVendorConnection = z.infer<typeof coachVendorConnectionSchema>;

export type CoachDepositView = 'add' | 'update' | 'ready' | 'unavailable' | 'loading';

export function resolveCoachDepositView(input: {
  hasStoredVendor: boolean;
  isLoading: boolean;
  isError: boolean;
  connection?: Pick<CoachVendorConnection, 'depositInfoStatus' | 'verificationUnavailable'>;
}): CoachDepositView {
  if (!input.hasStoredVendor) {
    return 'add';
  }
  if (input.isLoading) {
    return 'loading';
  }
  if (input.isError || !input.connection || input.connection.verificationUnavailable) {
    return 'unavailable';
  }

  switch (input.connection.depositInfoStatus) {
    case coachPaymentDepositInfoStatusSchema.enum.not_ready:
      return 'add';
    case coachPaymentDepositInfoStatusSchema.enum.missing_or_invalid:
      return 'update';
    case coachPaymentDepositInfoStatusSchema.enum.ready:
      return 'ready';
  }
}

export const createVendorType = paymentAccountTypeKind.extract(['ACH', 'INTERNATIONAL_WIRE']);

export const createVendorSchema = z.object({
  type: createVendorType,
  companyName: z.string().trim().describe('Teacher name'),
  routingNumber: z.string(),
  accountNumber: z.string(),
  accountClass: accountClassEnum,
  accountType: accountTypeEnum,
  email: z.string().email().optional(),
  phone: z.string().optional(),
});

export type CreateVendor = z.infer<typeof createVendorSchema>;

export const updateVendorSchema = z.object({
  companyName: z.string().trim().describe('Teacher name').optional(),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  type: createVendorType,
  routingNumber: z.string(),
  accountNumber: z.string(),
  accountClass: accountClassEnum,
  accountType: accountTypeEnum,
});

export type UpdateVendor = z.infer<typeof updateVendorSchema>;

const vendorACHSchema = vendor.extend({
  payment_accounts: z.object({
    details: maskedVendorACHSchema,
  }),
});

export type VendorACH = z.infer<typeof vendorACHSchema>;

const vendorDomesticWireSchema = vendor.extend({
  payment_accounts: z.object({
    details: maskedVendorDomesticWireSchema,
  }),
});

export type VendorDomesticWire = z.infer<typeof vendorDomesticWireSchema>;

const vendorInternationalWireSchema = vendor.extend({
  payment_accounts: z.object({
    details: maskedVendorInternationalWireSchema,
  }),
});

export type VendorInternationalWire = z.infer<typeof vendorInternationalWireSchema>;

export const brexAccountTypeToAirReading = {
  [paymentAccountTypeKind.enum.ACH]: paymentAccountType.enum.ach,
  [paymentAccountTypeKind.enum.INTERNATIONAL_WIRE]: paymentAccountType.enum.international_wire,
  [paymentAccountTypeKind.enum.DOMESTIC_WIRE]: paymentAccountType.enum.domestic_wire,
  [paymentAccountTypeKind.enum.CHEQUE]: paymentAccountType.enum.cheque,
} as const;

export const bulkSendTeacherPaymentSchema = getPayCycleFilterSchema;

export type BulkSendTeacherPayment = z.infer<typeof bulkSendTeacherPaymentSchema>;

export const bulkSendTeacherPaymentSuccessSchema = z.object({
  id: z.string(),
});

export type BulkSendTeacherPaymentSuccess = z.infer<typeof bulkSendTeacherPaymentSuccessSchema>;

export const bulkSendTeacherPaymentFailureReasonKind = z.enum([
  'missing_information',
  'payment_date_in_future',
  'payment_already_processed',
  'payment_already_in_progress',
  'stale_deposit_info',
  'brex_unavailable',
  'unknown',
]);

export type BulkSendTeacherPaymentFailureReason = z.infer<typeof bulkSendTeacherPaymentFailureReasonKind>;

export const bulkSendTeacherPaymentFailureReasonToLabel = {
  [bulkSendTeacherPaymentFailureReasonKind.enum.missing_information]: 'missing payment information',
  [bulkSendTeacherPaymentFailureReasonKind.enum.payment_date_in_future]: 'payment date in future',
  [bulkSendTeacherPaymentFailureReasonKind.enum.payment_already_processed]: 'payment already processed',
  [bulkSendTeacherPaymentFailureReasonKind.enum.payment_already_in_progress]: 'payment already in progress',
  [bulkSendTeacherPaymentFailureReasonKind.enum.stale_deposit_info]:
    'direct deposit is no longer valid in Brex; re-enter bank details',
  [bulkSendTeacherPaymentFailureReasonKind.enum.brex_unavailable]:
    'could not verify direct deposit with Brex; try again',
  [bulkSendTeacherPaymentFailureReasonKind.enum.unknown]: 'unknown error',
};

export const bulkSendTeacherPaymentFailureSchema = z.object({
  id: z.string(),
  reason: bulkSendTeacherPaymentFailureReasonKind,
});

export type BulkSendTeacherPaymentFailure = z.infer<typeof bulkSendTeacherPaymentFailureSchema>;

export class BulkSendTeacherPaymentSummary extends Summary<
  BulkSendTeacherPaymentSuccess,
  BulkSendTeacherPaymentFailure
> {}

export const bulkMarkTeacherPaymentAsSentSchema = getPayCycleFilterSchema;

export type BulkMarkTeacherPaymentAsSent = z.infer<typeof bulkMarkTeacherPaymentAsSentSchema>;

export const bulkMarkTeacherPaymentAsSentSuccessSchema = z.object({
  id: z.string(),
});

export type BulkMarkTeacherPaymentAsSentSuccess = z.infer<typeof bulkMarkTeacherPaymentAsSentSuccessSchema>;

export const bulkMarkTeacherPaymentAsSentFailureReasonKind = z.enum(['payment_already_processed', 'unknown']);

export type BulkMarkTeacherPaymentAsSentFailureReason = z.infer<typeof bulkMarkTeacherPaymentAsSentFailureReasonKind>;

export const bulkMarkTeacherPaymentAsSentFailureReasonToLabel = {
  [bulkMarkTeacherPaymentAsSentFailureReasonKind.enum.payment_already_processed]: 'payment already marked as sent',
  [bulkMarkTeacherPaymentAsSentFailureReasonKind.enum.unknown]: 'unknown error',
};

export const bulkMarkTeacherPaymentAsSentFailureSchema = z.object({
  id: z.string(),
  reason: bulkMarkTeacherPaymentAsSentFailureReasonKind,
});

export type BulkMarkTeacherPaymentAsSentFailure = z.infer<typeof bulkMarkTeacherPaymentAsSentFailureSchema>;

export class BulkMarkTeacherPaymentAsSentSummary extends Summary<
  BulkMarkTeacherPaymentAsSentSuccess,
  BulkMarkTeacherPaymentAsSentFailure
> {}

export const updatePaymentNotesSchema = z.object({
  notes: z.array(coachPaymentNoteKind).optional(),
});

export type UpdatePaymentNotes = z.infer<typeof updatePaymentNotesSchema>;

export const manualSyncPaymentsSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

export type ManualSyncPayments = z.infer<typeof manualSyncPaymentsSchema>;

export interface CoachPaymentCsvSuccess {
  row: number;
}

export interface CoachPaymentCsvFailure {
  row: number;
  message: string;
}

export class CoachPaymentCsvSummary extends Summary<CoachPaymentCsvSuccess, CoachPaymentCsvFailure> {}
export type CoachPaymentCsvSummaryLike = SummaryLike<CoachPaymentCsvSuccess, CoachPaymentCsvFailure>;
